/**
 * PriceSimulatorPage 價格模擬器 (S9.2) — `/auth/pricing/simulator`.
 *
 * Customer + product + qty + date + source → inline PriceTracePanel content.
 * The simulation calls the SAME `/pricing/resolve` endpoint order entry uses
 * (`pricingApi.resolve` → MSW handler → the pure `resolvePrice`, reused
 * VERBATIM), so simulator == order-entry price for identical inputs — the
 * parity the S9.2 property test (`priceTraceSimulator.test.ts`) pins down.
 *
 * Parity notes:
 * - The product picker is gated to Ready products — the same gate order
 *   entry's line editor applies (non-Ready products are ABSENT, not disabled).
 * - Source「自動」matches order entry, which never restricts the source
 *   (best available cost record wins).
 * - Customer is optional: no customer previews the list-price path (the
 *   resolver's no_customer skip is part of the rendered trace).
 *
 * Also hosts the 覆寫權限矩陣 card (plan §S9.2 override permission matrix):
 * Purchase Cost never; Landed Allocation / Internal Markup highest-role;
 * others role-gated; sales override = mandatory reason + 價格覆寫報表 row.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Col, DatePicker, InputNumber, Row, Select, Spin, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/common/PageHeader';
import TableContainer from '@/components/common/TableContainer';
import { PriceTraceContent } from '@/components/common/PriceTracePanel';
import { useCustomerOptions } from '@/hooks/useCustomers';
import { useReadyProducts } from '@/hooks/useSalesOrders';
import { pricingApi } from '@/services/api/pricing.api';
import { useFormStyles } from '@/styles/formStyles';
import { radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import type { PriceResolution, PriceSource } from '@/types/api/pricing';
import { SO_PRICE_DEVIATION_THRESHOLD_PCT } from '@/types/api/salesOrder';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Override permission matrix (plan §S9.2, per 討論用檔案) — data-driven so
// S10.1 can lift it into the role-permission matrix without a rewrite.
// ---------------------------------------------------------------------------

export type OverridePolicy = 'never' | 'highestRole' | 'roleGated' | 'reasonGated';

export interface OverridePermissionRule {
  /** i18n suffix under pricing.simulator.permissionMatrix.rows */
  key: string;
  policy: OverridePolicy;
}

// eslint-disable-next-line react-refresh/only-export-components
export const OVERRIDE_PERMISSION_MATRIX: readonly OverridePermissionRule[] = [
  { key: 'purchaseCost', policy: 'never' },
  { key: 'landedAllocation', policy: 'highestRole' },
  { key: 'internalMarkup', policy: 'highestRole' },
  { key: 'salesMarkup', policy: 'roleGated' },
  { key: 'specialPrice', policy: 'reasonGated' },
  { key: 'soLinePrice', policy: 'reasonGated' },
] as const;

const POLICY_TAG_COLOR: Record<OverridePolicy, string> = {
  never: 'red',
  highestRole: 'purple',
  roleGated: 'blue',
  reasonGated: 'orange',
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const SOURCE_OPTIONS: readonly PriceSource[] = ['CH', 'TW'] as const;

const PriceSimulatorPage = () => {
  const { t } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const elevation = useElevation();
  const formStyles = useFormStyles();

  const { customers, isLoading: customersLoading } = useCustomerOptions();
  const { products, isLoading: productsLoading } = useReadyProducts();

  const [customerId, setCustomerId] = useState<number | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [qty, setQty] = useState<number>(1);
  const [date, setDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [source, setSource] = useState<PriceSource | null>(null);

  const [resolution, setResolution] = useState<PriceResolution | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(false);

  // Auto-resolve on any input change — mirrors order entry's live re-resolve.
  // A request sequence guards against out-of-order responses.
  const requestSeqRef = useRef(0);
  useEffect(() => {
    if (!productId) {
      setResolution(null);
      setResolveError(false);
      return;
    }
    const seq = (requestSeqRef.current += 1);
    setResolving(true);
    setResolveError(false);
    pricingApi
      .resolve({
        productId,
        customerId,
        date,
        qty,
        ...(source ? { source } : {}),
      })
      .then(({ data }) => {
        if (requestSeqRef.current !== seq) return;
        setResolution(data);
        setResolving(false);
      })
      .catch(() => {
        if (requestSeqRef.current !== seq) return;
        setResolution(null);
        setResolving(false);
        setResolveError(true);
      });
  }, [productId, customerId, qty, date, source]);

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        value: product.id,
        label: `${product.sku} — ${product.displayName || product.name}`,
      })),
    [products]
  );

  const matrixColumns: TableProps<OverridePermissionRule>['columns'] = [
    {
      title: t('pricing:pricing.simulator.permissionMatrix.columns.field'),
      dataIndex: 'key',
      key: 'field',
      width: 200,
      render: (key: string) => (
        <Text style={{ fontWeight: token.fontWeightStrong }}>
          {t(`pricing:pricing.simulator.permissionMatrix.rows.${key}.field`)}
        </Text>
      ),
    },
    {
      title: t('pricing:pricing.simulator.permissionMatrix.columns.policy'),
      dataIndex: 'policy',
      key: 'policy',
      width: 190,
      render: (policy: OverridePolicy) => (
        <Tag color={POLICY_TAG_COLOR[policy]} style={{ margin: 0 }}>
          {t(`pricing:pricing.simulator.permissionMatrix.policies.${policy}`)}
        </Tag>
      ),
    },
    {
      title: t('pricing:pricing.simulator.permissionMatrix.columns.note'),
      dataIndex: 'key',
      key: 'note',
      render: (key: string) => (
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {t(`pricing:pricing.simulator.permissionMatrix.rows.${key}.note`, {
            pct: SO_PRICE_DEVIATION_THRESHOLD_PCT,
          })}
        </Text>
      ),
    },
  ];

  const sectionCardStyle = {
    backgroundColor: token.colorBgContainer,
    ...elevation.card,
    marginBottom: token.marginLG,
  };
  const sectionHeaderStyle = {
    padding: '18px 24px',
    backgroundColor: token.colorFillQuaternary,
    borderRadius: `${radiusTokens.card}px ${radiusTokens.card}px 0 0`,
  };

  return (
    <div className="tw-px-6 tw-pb-8">
      <PageHeader title={t('pricing:pricing.simulator.title')} />

      <Alert
        type="info"
        showIcon
        message={t('pricing:pricing.simulator.parityHint')}
        style={{ marginBottom: token.marginLG }}
      />

      {/* 模擬條件 Simulation inputs */}
      <div style={sectionCardStyle}>
        <div style={sectionHeaderStyle}>
          <Text strong>{t('pricing:pricing.simulator.paramsTitle')}</Text>
        </div>
        <div style={{ padding: '24px' }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>
                {t('pricing:pricing.simulator.fields.customer')}
              </Text>
              <Select<number>
                value={customerId ?? undefined}
                onChange={(value) => setCustomerId(value ?? null)}
                allowClear
                options={customers.map((customer) => ({
                  value: customer.customerId,
                  label: `${customer.customerCode} — ${customer.customerName}`,
                }))}
                loading={customersLoading}
                showSearch
                optionFilterProp="label"
                placeholder={t('pricing:pricing.simulator.fields.customerPlaceholder')}
                size="large"
                style={formStyles.inputStyle}
              />
            </Col>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('pricing:pricing.simulator.fields.product')}
              </Text>
              <Select<string>
                value={productId ?? undefined}
                onChange={(value) => setProductId(value ?? null)}
                options={productOptions}
                loading={productsLoading}
                showSearch
                optionFilterProp="label"
                placeholder={t('pricing:pricing.simulator.fields.productPlaceholder')}
                notFoundContent={
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    {t('pricing:pricing.simulator.fields.readyGateHint')}
                  </Text>
                }
                size="large"
                style={formStyles.inputStyle}
              />
            </Col>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>
                {t('pricing:pricing.simulator.fields.source')}
              </Text>
              <Select<PriceSource | ''>
                value={source ?? ''}
                onChange={(value) => setSource(value === '' ? null : value)}
                options={[
                  { value: '', label: t('pricing:pricing.simulator.fields.sourceAuto') },
                  ...SOURCE_OPTIONS.map((code) => ({
                    value: code,
                    label: t(`pricing:pricing.sources.${code}`),
                  })),
                ]}
                size="large"
                style={formStyles.inputStyle}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>{t('pricing:pricing.simulator.fields.qty')}</Text>
              <InputNumber
                value={qty}
                min={1}
                precision={0}
                onChange={(value) => {
                  if (value !== null) setQty(value);
                }}
                size="large"
                style={formStyles.inputStyle}
                aria-label={t('pricing:pricing.simulator.fields.qty')}
              />
            </Col>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>{t('pricing:pricing.simulator.fields.date')}</Text>
              <DatePicker
                value={dayjs(date)}
                allowClear={false}
                onChange={(value) => {
                  if (value) setDate(value.format('YYYY-MM-DD'));
                }}
                size="large"
                style={formStyles.inputStyle}
              />
            </Col>
          </Row>
        </div>
      </div>

      {/* 模擬結果 Result — the shared PriceTracePanel content, inline */}
      <div style={sectionCardStyle}>
        <div style={sectionHeaderStyle}>
          <Text strong>{t('pricing:pricing.simulator.resultTitle')}</Text>
        </div>
        <div style={{ padding: '24px' }}>
          {resolveError && (
            <Alert
              type="error"
              showIcon
              message={t('pricing:pricing.simulator.resolveFailed')}
              style={{ marginBottom: token.marginMD }}
            />
          )}
          {resolving ? (
            <div className="tw-flex tw-justify-center tw-py-8">
              <Spin />
            </div>
          ) : resolution ? (
            <div style={{ maxWidth: 480 }}>
              <PriceTraceContent
                resolution={resolution}
                unitPrice={resolution.unitPrice}
                currency={resolution.currency ?? 'AUD'}
              />
            </div>
          ) : (
            !resolveError && (
              <Text type="secondary">{t('pricing:pricing.simulator.empty')}</Text>
            )
          )}
        </div>
      </div>

      {/* 覆寫權限矩陣 Override permission matrix */}
      <div style={sectionCardStyle}>
        <div style={sectionHeaderStyle}>
          <Text strong>{t('pricing:pricing.simulator.permissionMatrix.title')}</Text>
        </div>
        <div style={{ padding: '24px' }}>
          <Text
            type="secondary"
            style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: token.marginSM }}
          >
            {t('pricing:pricing.simulator.permissionMatrix.hint')}
          </Text>
          <TableContainer>
            <Table<OverridePermissionRule>
              rowKey="key"
              columns={matrixColumns}
              dataSource={[...OVERRIDE_PERMISSION_MATRIX]}
              pagination={false}
              size="small"
            />
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default PriceSimulatorPage;
